import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, createAppAsyncThunk } from "appThunk";
import {
    selectComparator,
    selectStore
} from "modules/customer/insights/portfolioNew/portfolioSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { RootState } from "store";

import { AverageDailyFootfall, loadAverageDailyFootfall } from "./averageDailyFootfall";
import { FootfallOverTime, loadFootfallOverTime } from "./footfallOverTime";
import { DateTime } from "luxon";
import { max, median } from "mathjs";
import _, { maxBy } from "lodash";
import { FootfallGranularity } from "./footfallGranularity";
import { FootfallMeasureType } from "./footfallMeasureType";
import mathUtils from "utils/mathUtils";
import { FootfallCentile, loadFootfallCentiles } from "./footfallCentile";
import { FootfallByArea, loadFootfallByArea } from "./footfallByArea";

interface LoadFootfallResponse {
    averageDailyFootfall: AverageDailyFootfall[],
    footfallByArea: FootfallByArea[],
    footfallCentiles: FootfallCentile[],
    footfallOverTime: FootfallOverTime[]
}

interface FootfallState {
    isLoading: boolean,
    hasErrors: boolean,
    averageDailyFootfall: AverageDailyFootfall[],
    averageDailyFootfallMeasure: FootfallMeasureType,
    footfallByArea: FootfallByArea[],
    footfallByAreaMeasure: FootfallMeasureType,
    footfallCentiles: FootfallCentile[],
    footfallOverTime: FootfallOverTime[],
    footfallOverTimeGranularity: FootfallGranularity,
    footfallOverTimeMeasure: FootfallMeasureType
}

const initialState: FootfallState = {
    isLoading: false,
    hasErrors: false,
    averageDailyFootfall: [],
    averageDailyFootfallMeasure: FootfallMeasureType.NumberOfVisits,
    footfallByArea: [],
    footfallByAreaMeasure: FootfallMeasureType.NumberOfVisits,
    footfallCentiles: [],
    footfallOverTime: [],
    footfallOverTimeGranularity: FootfallGranularity.Month,
    footfallOverTimeMeasure: FootfallMeasureType.NumberOfVisits,
};

const footfallNewSlice = createSlice({
    name: "customer/insights/portfolioNew/footfallNew",
    initialState,
    reducers: {
        clearAverageDailyFootfall: (state) => {
            state.averageDailyFootfall = initialState.averageDailyFootfall;
            state.averageDailyFootfallMeasure = initialState.averageDailyFootfallMeasure;
        },
        clearFootfallByArea: (state) => {
            state.footfallByArea = initialState.footfallByArea;
            state.footfallByAreaMeasure = initialState.footfallByAreaMeasure;
        },
        clearFootfallCentiles: (state) => {
            state.footfallCentiles = initialState.footfallCentiles;
        },
        clearFootfallOverTime: (state) => {
            state.footfallOverTime = initialState.footfallOverTime;
            state.footfallOverTimeGranularity = initialState.footfallOverTimeGranularity;
            state.footfallOverTimeMeasure = initialState.footfallOverTimeMeasure;
        },
        setAverageDailyFootfallMeasure: (state, action: PayloadAction<FootfallMeasureType>) => {
            state.averageDailyFootfallMeasure = action.payload;
        },
        setFootfallByAreaMeasure: (state, action: PayloadAction<FootfallMeasureType>) => {
            state.footfallByAreaMeasure = action.payload;
        },
        setFootfallOverTimeGranularity: (state, action: PayloadAction<FootfallGranularity>) => {
            state.footfallOverTimeGranularity = action.payload;
        },
        setFootfallOverTimeMeasure: (state, action: PayloadAction<FootfallMeasureType>) => {
            state.footfallOverTimeMeasure = action.payload;
        }
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadFootfallNew.pending, (state: FootfallState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.averageDailyFootfall = initialState.averageDailyFootfall;
            state.footfallByArea = initialState.footfallByArea;
            state.footfallCentiles = initialState.footfallCentiles;
            state.footfallOverTime = initialState.footfallOverTime;
        });
        builder.addCase(loadFootfallNew.rejected, (state: FootfallState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.averageDailyFootfall = initialState.averageDailyFootfall;
            state.footfallByArea = initialState.footfallByArea;
            state.footfallCentiles = initialState.footfallCentiles;
            state.footfallOverTime = initialState.footfallOverTime;
        });
        builder.addCase(loadFootfallNew.fulfilled, (state: FootfallState, action: PayloadAction<LoadFootfallResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.averageDailyFootfall = action.payload.averageDailyFootfall;
            state.footfallByArea = action.payload.footfallByArea;
            state.footfallCentiles = action.payload.footfallCentiles;
            state.footfallOverTime = action.payload.footfallOverTime;
        });
    }
});

export const {
    setAverageDailyFootfallMeasure,
    setFootfallByAreaMeasure,
    setFootfallOverTimeGranularity,
    setFootfallOverTimeMeasure
} = footfallNewSlice.actions;

export const loadFootfallNew = createAppAsyncThunk(
    "customer/insights/portfolioNew/footfallNew/loadFootfallNew",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const selectedStore = selectStore(state);
            const comparator = selectComparator(state);

            const comparatorStores = comparator?.getStores() ?? [];
            const selectedAndComparatorStores = selectedStore ? [selectedStore].concat(comparatorStores) : [];

            const averageDailyFootfallPromise = thunkAPI.dispatch(loadAverageDailyFootfall(selectedStore?.retailCentreID));
            const footfallByAreaPromise = thunkAPI.dispatch(loadFootfallByArea(selectedStore));
            const footfallCentilesPromise = thunkAPI.dispatch(loadFootfallCentiles(selectedAndComparatorStores));
            const footfallOverTimePromise = thunkAPI.dispatch(loadFootfallOverTime(selectedAndComparatorStores));

            const results = await Promise.all([
                averageDailyFootfallPromise,
                footfallByAreaPromise,
                footfallCentilesPromise,
                footfallOverTimePromise
            ]);
            const averageDailyFootfall = results[0];
            const footfallByArea = results[1];
            const footfallCentiles = results[2];
            const footfallOverTime = results[3];
            const loadFootfallResponse: LoadFootfallResponse = {
                averageDailyFootfall,
                footfallByArea,
                footfallCentiles,
                footfallOverTime
            };
            return loadFootfallResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Footfall.", error));
            return thunkAPI.rejectWithValue(null);
        }
    }
);

export const clearFootfallNew = (): AppThunk => (dispatch) => {
    dispatch(footfallNewSlice.actions.clearAverageDailyFootfall());
    dispatch(footfallNewSlice.actions.clearFootfallByArea());
    dispatch(footfallNewSlice.actions.clearFootfallCentiles());
    dispatch(footfallNewSlice.actions.clearFootfallOverTime());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.hasErrors;
};

export const selectAverageDailyFootfall = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.averageDailyFootfall;
};

export const selectAverageDailyFootfallMeasure = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.averageDailyFootfallMeasure;
};

export const selectFootfallByArea = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.footfallByArea;
};

export const selectFootfallByAreaMeasure = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.footfallByAreaMeasure;
};

export const selectFootfallCentiles = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.footfallCentiles;
};

export const selectFootfallOverTime = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.footfallOverTime;
};

export const selectFootfallOverTimeGranularity = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.footfallOverTimeGranularity;
};

export const selectFootfallOverTimeMeasure = (state: RootState) => {
    return state.customer.insights.portfolioNew.footfallNew.footfallOverTimeMeasure;
};

export const selectLastTwelveMonthsTotalVisits = createSelector(
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    selectFootfallOverTime,
    (selectedStore, comparator, footfallOverTime) => {
        const totalVisits = {
            selectedStore: 0,
            comparator: 0,
            percentageDifference: 0
        };
        if (!selectedStore || !comparator || footfallOverTime.length === 0) {
            return totalVisits;
        }

        const mostRecentItem = maxBy(footfallOverTime, item => item.date.toMillis());
        const twelveMonthsAgoDate = mostRecentItem?.date.minus({ years: 1 });
        const previousTwelveMonthsFootfall = twelveMonthsAgoDate ? footfallOverTime.filter(item => item.date > twelveMonthsAgoDate) : [];

        const selectedStoreFootfall = previousTwelveMonthsFootfall.filter(item => item.retailCentreId === selectedStore.retailCentreID);
        totalVisits.selectedStore = selectedStoreFootfall.reduce((acc, item) => acc + item.numberOfVisits, 0);

        const comparatorRetailCentreIds = comparator.getStores().map(store => store.retailCentreID);
        const comparatorFootfall = previousTwelveMonthsFootfall.filter(item => comparatorRetailCentreIds.includes(item.retailCentreId));

        const comparatorValues = _(comparatorFootfall)
            .groupBy(item => item.retailCentreId)
            .map((group, retailCentreId) => ({
                retailCentreId,
                numberOfVisits: group.reduce((acc, item) => acc + item.numberOfVisits, 0)
            }))
            .value();

        totalVisits.comparator = comparatorValues.length === 0
            ? 0
            : median(comparatorValues.map(item => item.numberOfVisits));

        totalVisits.percentageDifference = mathUtils.safePercentageChange(totalVisits.selectedStore, totalVisits.comparator);

        return totalVisits;
    }
);

export const selectLatestMonthFootfall = createSelector(
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    selectFootfallOverTime,
    (selectedStore, comparator, footfallOverTime) => {
        if (!selectedStore || !comparator || footfallOverTime.length === 0) {
            return {
                latestMonthDate: DateTime.fromMillis(0),
                numberOfVisits: { selectedStore: 0, comparator: 0, percentageDifference: 0 },
                dwellTime: { selectedStore: 0, comparator: 0, percentageDifference: 0 }
            };
        }

        const mostRecentMillis = max(footfallOverTime.map(item => item.date.toMillis()));
        const mostRecentDate = DateTime.fromMillis(mostRecentMillis, { zone: "utc" });
        const latestMonthFootfallValues = footfallOverTime.filter(item => item.date.equals(mostRecentDate));

        const selectedStoreFootfall = latestMonthFootfallValues.find(item =>
            item.retailCentreId === selectedStore.retailCentreID
        ) ?? { numberOfVisits: 0, dwellTime: 0 };

        const comparatorRetailCentreIds = comparator.getStores().map(store => store.retailCentreID);
        const comparatorValues = latestMonthFootfallValues.filter(item => comparatorRetailCentreIds.includes(item.retailCentreId));

        const comparatorMedian = {
            numberOfVisits: comparatorValues.length === 0 ? 0 : median(comparatorValues.map(item => item.numberOfVisits)),
            dwellTime: comparatorValues.length === 0 ? 0 : median(comparatorValues.map(item => item.dwellTime))
        };

        return {
            latestMonthDate: mostRecentDate,
            numberOfVisits: {
                selectedStore: selectedStoreFootfall.numberOfVisits,
                comparator: comparatorMedian.numberOfVisits,
                percentageDifference: mathUtils.safePercentageChange(selectedStoreFootfall.numberOfVisits, comparatorMedian.numberOfVisits)
            },
            dwellTime: {
                selectedStore: selectedStoreFootfall.dwellTime,
                comparator: comparatorMedian.dwellTime,
                percentageDifference: mathUtils.safePercentageChange(selectedStoreFootfall.dwellTime, comparatorMedian.dwellTime)
            }
        };
    }
);

interface StoreFootfallOverTime {
    selectedStore: FootfallByDate[],
    comparator: FootfallByDate[]
}

interface FootfallByDate {
    date: DateTime,
    numberOfVisits: number,
    dwellTime: number
}

export const selectStoreFootfallOverTime = createSelector(
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    selectFootfallOverTime,
    (selectedStore, comparator, footfallOverTime) => {
        const storeFootfallOverTime: StoreFootfallOverTime = {
            selectedStore: [],
            comparator: []
        };
        if (!selectedStore || !comparator || footfallOverTime.length === 0) {
            return storeFootfallOverTime;
        }

        const selectedStoreFootfall = footfallOverTime.filter(item => item.retailCentreId === selectedStore.retailCentreID);
        storeFootfallOverTime.selectedStore = selectedStoreFootfall.map(item => ({
            date: item.date,
            numberOfVisits: item.numberOfVisits,
            dwellTime: item.dwellTime
        }));

        const comparatorRetailCentreIds = comparator.getStores().map(store => store.retailCentreID);
        const comparatorFootfall = footfallOverTime.filter(item => comparatorRetailCentreIds.includes(item.retailCentreId));

        storeFootfallOverTime.comparator = _(comparatorFootfall)
            .groupBy(item => item.date.toMillis())
            .map((group, key) => ({
                date: DateTime.fromMillis(Number(key), { zone: 'utc' }),
                numberOfVisits: (group.length !== 0) ? median(group.map(item => item.numberOfVisits)) : 0,
                dwellTime: (group.length !== 0) ? median(group.map(item => item.dwellTime)) : 0
            }))
            .value();

        return storeFootfallOverTime;
    }
);

export const selectStoreFootfallCentiles = createSelector(
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    selectFootfallCentiles,
    (selectedStore, comparator, centiles) => {
        const storeCentiles = {
            selectedStore: 0,
            comparator: 0
        };
        if (!selectedStore || !comparator || centiles.length === 0) {
            return storeCentiles;
        }

        storeCentiles.selectedStore = centiles.find(item => item.retailCentreId === selectedStore.retailCentreID)?.centile ?? 0;

        const comparatorRetailCentreIds = comparator.getStores().map(store => store.retailCentreID);
        const comparatorCentiles = centiles
            .filter(item => comparatorRetailCentreIds.includes(item.retailCentreId))
            .map(item => item.centile);

        storeCentiles.comparator = comparatorCentiles.length !== 0 ? median(comparatorCentiles) : 0;
        return storeCentiles;
    }
);

const FOOTFALL_MAP_CENTILES = [0, 0.4, 0.8, 0.9, 0.95];

export const selectFootfallByAreaMapThresholds = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFootfallByArea,
    (isLoading, hasErrors, footfallByArea) => {
        if (isLoading || hasErrors || footfallByArea.length === 0) {
            return { visitsThresholds: [], dwellThresholds: [] };
        }

        const visitsValues = footfallByArea.map(item => item.numberOfVisits);
        const dwellValues = footfallByArea.map(item => item.dwellTime);

        const visitsThresholds = mathUtils.customPercentileThresholds(visitsValues, FOOTFALL_MAP_CENTILES);
        const dwellThresholds = mathUtils.customPercentileThresholds(dwellValues, FOOTFALL_MAP_CENTILES);

        return { visitsThresholds, dwellThresholds };
    }
);

export interface FootfallByAreaWithColourGroups extends FootfallByArea {
    visitsColourGroup: number,
    dwellColourGroup: number
}

export const selectFootfallByAreaWithColourGroups = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFootfallByArea,
    selectFootfallByAreaMapThresholds,
    (isLoading, hasErrors, footfallByArea, thresholds) => {
        if (isLoading || hasErrors || footfallByArea.length === 0) {
            return [];
        }

        const { visitsThresholds, dwellThresholds } = thresholds;
        const footfallByAreaWithCentiles: FootfallByAreaWithColourGroups[] = footfallByArea.map(item => {
            let visitsColourGroup = 0;
            for (let i = 0; i < visitsThresholds.length; i++) {
                if (item.numberOfVisits >= visitsThresholds[i]) {
                    visitsColourGroup = (visitsThresholds.length - 1) - i;
                }
            }
            let dwellColourGroup = 0;
            for (let i = 0; i < dwellThresholds.length; i++) {
                if (item.dwellTime >= dwellThresholds[i]) {
                    dwellColourGroup = (dwellThresholds.length - 1) - i;
                }
            }
            return {
                ...item,
                visitsColourGroup,
                dwellColourGroup
            };
        });
        return footfallByAreaWithCentiles;
    }
);

export interface AverageDailyFootfallHeatMapData {
    footfallData: AverageDailyFootfall[],
    maxFootfallCount: number,
    maxDwellTime: number
}

export const selectAverageDailyFootfallHeatMapData = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectAverageDailyFootfall,
    (isLoading, hasErrors, averageDailyFootfall) => {
        const averageDailyFootfallData: AverageDailyFootfallHeatMapData = {
            footfallData: [],
            maxFootfallCount: 0,
            maxDwellTime: 0
        };

        if (isLoading || hasErrors || averageDailyFootfall.length === 0) {
            return averageDailyFootfallData;
        }

        const mostRecentMillis = max(averageDailyFootfall.map(item => item.monthDate.toMillis()));
        const mostRecentDate = DateTime.fromMillis(mostRecentMillis, { zone: "utc" });
        const latestMonthFootfall = averageDailyFootfall.filter(item => item.monthDate.equals(mostRecentDate));

        const sortedByFootfallCount = latestMonthFootfall.map(x => x.footfallCount).sort((a, b) => b - a);
        const maxFootfallCount = sortedByFootfallCount[0];

        const sortedByDwellTime = latestMonthFootfall.map(x => x.dwellTime).sort((a, b) => b - a);
        const maxDwellTime = sortedByDwellTime[0];

        averageDailyFootfallData.footfallData = latestMonthFootfall;
        averageDailyFootfallData.maxFootfallCount = maxFootfallCount;
        averageDailyFootfallData.maxDwellTime = maxDwellTime;

        return averageDailyFootfallData;
    }
);

export default footfallNewSlice;
